/**
 * Session state for the whole app.
 *
 * The server is the source of truth: this provider asks `/api/auth/me` once on
 * mount, then updates only after a call the server accepted. There is no
 * "logged in" flag in localStorage to drift out of sync with a revoked cookie.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ReactNode } from 'react';
import { Access, api, ApiError, AuthUser } from './api';

type AuthValue = {
  user: AuthUser | null;
  access: Access | null;
  loading: boolean;
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
  register(username: string, password: string, note: string): Promise<string>;
  refresh(): Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [access, setAccess] = useState<Access | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const result = await api<{ user: AuthUser; access: Access }>('/api/auth/me');
      setUser(result.user);
      setAccess(result.access);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setUser(null);
        setAccess(null);
        return;
      }
      // A network failure must not pretend the account was revoked; keep the
      // last known state and let the next action surface the real error.
      if (!(error instanceof ApiError)) return;
      setUser(null);
      setAccess(null);
    }
  }, []);

  useEffect(() => {
    void refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    const result = await api<{ user: AuthUser; access: Access }>('/api/auth/login', {
      method: 'POST',
      body: { username, password },
    });
    setUser(result.user);
    setAccess(result.access);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } finally {
      setUser(null);
      setAccess(null);
    }
  }, []);

  const register = useCallback(async (username: string, password: string, note: string) => {
    const result = await api<{ message: string }>('/api/auth/register', {
      method: 'POST',
      body: { username, password, note },
    });
    return result.message;
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ user, access, loading, login, logout, register, refresh }),
    [user, access, loading, login, logout, register, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}

/**
 * The same context for components that also render in the offline desktop
 * shell, where no AuthProvider is mounted at all. `null` means "not the hosted
 * edition", not "signed out".
 */
export function useOptionalAuth(): AuthValue | null {
  return useContext(AuthContext);
}
